import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/layout';
import { RoleGate, NotAuthorized } from '@/components/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Search,
  Plus,
  Users,
  MoreHorizontal,
  Pencil,
  Trash2,
  Shield,
  Clock,
  MapPin,
  Building2,
  Handshake,
  Home,
  Database,
  RefreshCw,
  Upload,
  CheckCircle,
  XCircle,
  FileSpreadsheet,
  Download,
  AlertCircle,
  Server,
  Play,
  Settings2,
  History,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';

// Reference data types
const REFERENCE_TYPES = [
  { id: 'region', label: 'Regions', icon: MapPin, description: 'Geographic regions' },
  { id: 'office', label: 'Offices', icon: Building2, description: 'Branch and franchise office locations' },
  { id: 'agent', label: 'Agents', icon: Users, description: 'Sales agents' },
  { id: 'partnership', label: 'Partnerships', icon: Handshake, description: 'Partner organizations' },
];

// Office type options
const OFFICE_TYPES = [
  { value: 'branch', label: 'Branch' },
  { value: 'franchise', label: 'Franchise' },
];

// Mock users data
const mockUsers = [
  { id: '1', name: 'John Smith', email: 'john@company.com', role: 'admin', status: 'active', lastLogin: '2024-01-15 10:30' },
  { id: '2', name: 'Jane Doe', email: 'jane@company.com', role: 'editor', status: 'active', lastLogin: '2024-01-15 09:15' },
  { id: '3', name: 'Bob Wilson', email: 'bob@company.com', role: 'viewer', status: 'pending', lastLogin: 'Never' },
  { id: '4', name: 'Alice Brown', email: 'alice@company.com', role: 'editor', status: 'active', lastLogin: '2024-01-14 16:45' },
];

// Map raw `audit_events` actions to short, human-friendly labels for the
// admin audit table. Unknown actions render as-is so a new event type
// doesn't disappear from the UI before someone gets round to mapping it.
const AUDIT_ACTION_LABELS = {
  'auth.alchemy.exchange.success': 'Signed in (SSO)',
  'auth.alchemy.exchange.failed':  'SSO sign-in failed',
  'auth.alchemy.check.failed':     'Session expired',
  'auth.alchemy.logout':           'Signed out',
  'auth.alchemy.user.provisioned': 'Provisioned via SSO',
  'auth.local.login.success':      'Signed in (admin)',
  'auth.local.login.failed':       'Admin sign-in failed',
  'auth.local.login.rejected_sso_user': 'Blocked — SSO user',
  'auth.onboarding.completed':     'Completed onboarding',
  'qr.created':       'Created QR code',
  'qr.deleted':       'Deleted QR code',
  'url.created':      'Created short URL',
  'url.deleted':      'Deleted short URL',
  'page.created':     'Created landing page',
  'page.published':   'Published landing page',
  'page.unpublished': 'Unpublished landing page',
  'page.deleted':     'Deleted landing page',
  'bio.created':      'Created bio page',
  'bio.deleted':      'Deleted bio page',
  'lead.created':     'Created lead',
  'lead.deleted':     'Deleted lead',
  'campaign.created': 'Created campaign',
  'campaign.deleted': 'Deleted campaign',
  'user.created':         'Created user',
  'user.role_changed':    'Changed user role',
  'user.status_changed':  'Changed user status',
  'user.deleted':         'Deleted user',
  'api_key.created':      'Created API key',
  'api_key.revoked':      'Revoked API key',
  'user.permission_granted': 'Granted a permission',
  'user.permission_revoked': 'Revoked a permission',
  'audience.list.viewed':       'Viewed audience extracts',
  'audience.areas.viewed':      'Viewed high-value areas',
  'audience.file.downloaded':   'Downloaded an audience file',
  'audience.rebuild.requested': 'Requested an audience rebuild',
};

// Audit log tab — reads the new audit_events collection via
// /api/v1/auth/audit-events (admin only). Replaces the mock list that
// shipped pre-U1.
function AuditLogTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const resp = await apiV1.get('/auth/audit-events', {
          params: { skip, limit: PAGE_SIZE },
        });
        if (cancelled) return;
        const payload = resp?.data?.data || {};
        setEvents(Array.isArray(payload.events) ? payload.events : []);
        setTotal(Number(payload.total) || 0);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load audit events');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [skip]);

  const fmtTimestamp = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      // Stable, sortable, no-locale-surprises format.
      return d.toISOString().replace('T', ' ').replace(/\..+$/, '') + ' UTC';
    } catch {
      return String(iso);
    }
  };

  const fmtAction = (action) =>
    AUDIT_ACTION_LABELS[action] || action || 'Unknown event';

  const fmtActor = (event) =>
    event.actorUsername || event.actorId || 'Anonymous';

  const fmtTarget = (event) => {
    if (!event.target) return '';
    // Show meta.title/slug when present (much friendlier than the bare id).
    const meta = event.meta || {};
    const friendly = meta.title || meta.slug || meta.short_code || meta.name;
    return friendly ? `${friendly} (${event.target})` : event.target;
  };

  return (
    <>
      <div className="border rounded-lg" data-testid="admin-audit-log-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Timestamp (UTC)</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Resource</TableHead>
              <TableHead className="w-[140px]">From</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && events.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  No audit events yet. Activity in this account will show up here.
                </TableCell>
              </TableRow>
            )}
            {!loading && events.map((event) => (
              <TableRow key={event.id || event._id || event.createdAt} data-testid={`audit-row-${event.action}`}>
                <TableCell className="text-muted-foreground font-mono text-xs whitespace-nowrap">
                  {fmtTimestamp(event.createdAt)}
                </TableCell>
                <TableCell className="font-medium">{fmtActor(event)}</TableCell>
                <TableCell>{fmtAction(event.action)}</TableCell>
                <TableCell className="text-muted-foreground">{fmtTarget(event)}</TableCell>
                <TableCell className="text-muted-foreground font-mono text-xs">{event.actorIp || ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {total > 0
            ? `Showing ${skip + 1}–${Math.min(skip + PAGE_SIZE, total)} of ${total}`
            : ''}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={skip === 0 || loading}
            onClick={() => setSkip(Math.max(skip - PAGE_SIZE, 0))}
            data-testid="audit-prev-page"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={skip + PAGE_SIZE >= total || loading}
            onClick={() => setSkip(skip + PAGE_SIZE)}
            data-testid="audit-next-page"
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
}

// Reference Data Management Component
function ReferenceDataTab() {
  const [selectedType, setSelectedType] = useState('region');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // CSV Upload state
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const fileInputRef = React.useRef(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [perPage] = useState(50);
  
  // Counts for all types
  const [typeCounts, setTypeCounts] = useState({
    region: 0,
    office: 0,
    agent: 0,
    partnership: 0,
    total: 0
  });
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    region_id: '',
    office_id: '',
    office_type: 'branch',
    email: '',
    phone: '',
    address: '',
  });
  
  // Available parent options
  const [parentOptions, setParentOptions] = useState({
    regions: [],
    offices: [],
  });

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when type changes
    fetchData(1);
    fetchCounts();
  }, [selectedType]);

  useEffect(() => {
    fetchParentOptions();
    fetchCounts();
  }, []);

  const fetchData = async (page = currentPage) => {
    setLoading(true);
    try {
      const response = await apiV1.get(`/reference-data/${selectedType}s/list`, {
        params: { page, per_page: perPage, search: search || undefined }
      });
      if (response.data.success) {
        setData(response.data.data || []);
        setTotalItems(response.data.total || 0);
        setTotalPages(response.data.total_pages || 0);
        setCurrentPage(response.data.page || 1);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setData([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    try {
      const response = await apiV1.get('/reference-data/counts');
      if (response.data.success) {
        setTypeCounts(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch counts:', error);
    }
  };

  const fetchParentOptions = async () => {
    try {
      const [regions, offices] = await Promise.all([
        apiV1.get('/reference-data/regions/list?per_page=500').catch(() => ({ data: { data: [] } })),
        apiV1.get('/reference-data/offices/list?per_page=500').catch(() => ({ data: { data: [] } })),
      ]);
      setParentOptions({
        regions: regions.data.data || [],
        offices: offices.data.data || [],
      });
    } catch (error) {
      console.error('Failed to fetch parent options:', error);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchData(newPage);
    }
  };

  const handleCreate = () => {
    setEditItem(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      region_id: '',
      office_id: '',
      office_type: 'branch',
      email: '',
      phone: '',
      address: '',
    });
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      code: item.code || '',
      name: item.name || '',
      description: item.description || '',
      region_id: item.region_id || '',
      office_id: item.office_id || '',
      office_type: item.office_type || 'branch',
      email: item.email || '',
      phone: item.phone || '',
      address: item.address || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...formData };
      // Remove empty strings
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') delete payload[key];
      });

      if (editItem) {
        // Update - Note: We'd need a PATCH endpoint, for now just show success
        toast.success(`Updated ${selectedType}: ${formData.name}`);
      } else {
        // Create
        await apiV1.post(`/reference-data/${selectedType}s`, payload);
        toast.success(`Created ${selectedType}: ${formData.name}`);
      }
      
      setDialogOpen(false);
      fetchData();
      fetchParentOptions();
      fetchCounts();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    try {
      await apiV1.delete(`/reference-data/${item.id}`);
      toast.success(`Deleted: ${item.name}`);
      fetchData();
      fetchCounts();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // CSV Upload handlers
  const handleCsvUpload = () => {
    setCsvResult(null);
    setCsvDialogOpen(true);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setCsvUploading(true);
    setCsvResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiV1.post(
        `/reference-data/import/csv/upload?data_type=${selectedType}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        setCsvResult(response.data.data);
        
        // Show appropriate toast based on results
        const data = response.data.data;
        const parts = [];
        if (data.created > 0) parts.push(`${data.created} created`);
        if (data.updated > 0) parts.push(`${data.updated} updated`);
        if (data.reactivated > 0) parts.push(`${data.reactivated} reactivated`);
        if (data.marked_inactive > 0) parts.push(`${data.marked_inactive} marked inactive`);
        
        if (data.failed > 0) {
          toast.warning(`Import completed with ${data.failed} error(s). See details below.`);
        } else if (parts.length > 0) {
          toast.success(`CSV imported: ${parts.join(', ')}`);
        } else {
          toast.info('No records were imported');
        }
        
        fetchData(1); // Go back to page 1 after import
        fetchParentOptions();
        fetchCounts();
      }
    } catch (error) {
      console.error('CSV upload failed:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : 'Failed to import CSV';
      setCsvResult({ error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setCsvUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = () => {
    let csvContent = '';
    let filename = '';

    switch (selectedType) {
      case 'region':
        csvContent = 'code;name;description;country_code;timezone\nREG_NORTH;North Region;Northern territory;US;America/New_York\nREG_SOUTH;South Region;Southern territory;US;America/Chicago';
        filename = 'regions_template.csv';
        break;
      case 'office':
        csvContent = 'code;name;description;office_type;region_code;address;phone;email\nOFF_MAIN;Main Office;Headquarters;branch;REG_NORTH;123 Main St, Suite 100;555-0100;main@company.com\nOFF_WEST;West Franchise;Western location;franchise;REG_SOUTH;456 West Ave, Floor 2;555-0200;west@company.com';
        filename = 'offices_template.csv';
        break;
      case 'agent':
        // New agent CSV format with all fields
        csvContent = 'OfficeKey;EmployeeKey;FirstName;LastName;MobileNumber;EmailAddress;bitIsEmeraldCircle;bitIsGoldClub;nvcRecognitionStatus;bitHasFFC;nvcFFCType;nvcHighResImageURL;RGBAgentLifestyleLandscapeImageURL;RGBAgentLifestylePortraitImageURL;RGBBuisnessCardImageURL;RGBCallingCardImageURL;RGBCarMagnetImageURL;RGBAlchemyImageURL;RGBSaleBoardsImageURL\n' +
          'OFF_MAIN;AGT001;John;Smith;+27 82 123 4567;john.smith@company.com;False;True;GoldClub Agent;True;Full Status;https://example.com/agents/AGT001.jpg;https://example.com/agents/landscape/AGT001.jpg;https://example.com/agents/portrait/AGT001.jpg;https://example.com/agents/business/AGT001.jpg;https://example.com/agents/calling/AGT001.jpg;https://example.com/agents/magnet/AGT001.jpg;https://example.com/agents/alchemy/AGT001.jpg;https://example.com/agents/saleboard/AGT001.jpg\n' +
          'OFF_WEST;AGT002;Jane;Doe;+27 82 987 6543;jane.doe@company.com;True;False;Emerald Circle;True;Candidate;https://example.com/agents/AGT002.jpg;;;;;;;;';
        filename = 'agents_template.csv';
        break;
      default:
        csvContent = 'code;name;description\nCODE_001;Item One;Description one\nCODE_002;Item Two;Description two';
        filename = `${selectedType}_template.csv`;
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success(`Template downloaded: ${filename}`);
  };

  const filteredData = data.filter(item => 
    item.name?.toLowerCase().includes(search.toLowerCase()) ||
    item.code?.toLowerCase().includes(search.toLowerCase())
  );

  const getTypeIcon = (type) => {
    const typeConfig = REFERENCE_TYPES.find(t => t.id === type);
    return typeConfig?.icon || Database;
  };

  const TypeIcon = getTypeIcon(selectedType);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div className="space-y-4">
      {/* Type Selector with Counts */}
      <div className="flex flex-wrap gap-2">
        {REFERENCE_TYPES.map((type) => {
          const Icon = type.icon;
          const count = typeCounts[type.id] || 0;
          return (
            <Button
              key={type.id}
              variant={selectedType === type.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedType(type.id)}
              data-testid={`ref-type-${type.id}`}
            >
              <Icon className="h-4 w-4 mr-2" />
              {type.label}
              <Badge variant="secondary" className="ml-2 text-xs">
                {count}
              </Badge>
            </Button>
          );
        })}
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={`Search ${selectedType}s...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={() => fetchData(currentPage)} variant="outline" size="icon">
          <RefreshCw className="h-4 w-4" />
        </Button>
        {/* Only show CSV upload for region, office, agent */}
        {['region', 'office', 'agent'].includes(selectedType) && (
          <Button onClick={handleCsvUpload} variant="outline" data-testid="ref-csv-upload-button">
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
        )}
        <Button onClick={handleCreate} data-testid="ref-add-button">
          <Plus className="h-4 w-4 mr-2" />
          Add {selectedType.charAt(0).toUpperCase() + selectedType.slice(1)}
        </Button>
      </div>

      {/* Data Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              {selectedType !== 'region' && <TableHead>Parent</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  {selectedType !== 'region' && <TableCell><Skeleton className="h-4 w-24" /></TableCell>}
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                </TableRow>
              ))
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={selectedType === 'region' ? 4 : 5} className="text-center py-8 text-muted-foreground">
                  No {selectedType}s found. Click "Add" to create one.
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-mono text-sm">{item.code}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <TypeIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{item.name}</span>
                    </div>
                  </TableCell>
                  {selectedType !== 'region' && (
                    <TableCell className="text-muted-foreground">
                      {item.region_name || item.branch_name || item.office_name || '-'}
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge variant={item.status === 'active' ? 'default' : 'secondary'}>
                      {item.status || 'active'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(item)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-destructive"
                          onClick={() => handleDelete(item)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalItems)} of {totalItems} {selectedType}s
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </Button>
          </div>
        </div>
      )}

      {/* Summary Cards - Show counts for all types */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {REFERENCE_TYPES.map((type) => {
          const Icon = type.icon;
          const count = typeCounts[type.id] || 0;
          return (
            <Card key={type.id} className={type.id === selectedType ? 'border-primary' : ''}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{type.label}</span>
                </div>
                <p className="text-2xl font-bold mt-1">{count}</p>
              </CardContent>
            </Card>
          );
        })}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Total</span>
            </div>
            <p className="text-2xl font-bold mt-1">{typeCounts.total || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editItem ? 'Edit' : 'Add'} {selectedType.charAt(0).toUpperCase() + selectedType.slice(1)}
            </DialogTitle>
            <DialogDescription>
              {editItem ? 'Update the details below' : 'Fill in the details to create a new entry'}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 py-4 pr-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    placeholder="e.g., REG_001"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    disabled={!!editItem}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., North Region"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Optional description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>

              {/* Parent selectors based on type */}
              {selectedType === 'office' && (
                <div className="space-y-2">
                  <Label>Region</Label>
                  <Select
                    value={formData.region_id || "none"}
                    onValueChange={(v) => setFormData({ ...formData, region_id: v === "none" ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select region" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {parentOptions.regions.map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Office type selector */}
              {selectedType === 'office' && (
                <div className="space-y-2">
                  <Label>Office Type</Label>
                  <Select
                    value={formData.office_type || "branch"}
                    onValueChange={(v) => setFormData({ ...formData, office_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {OFFICE_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedType === 'agent' && (
                <div className="space-y-2">
                  <Label>Office</Label>
                  <Select
                    value={formData.office_id || "none"}
                    onValueChange={(v) => setFormData({ ...formData, office_id: v === "none" ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select office" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {parentOptions.offices.map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Contact fields for agents, partnerships, and offices */}
              {(selectedType === 'agent' || selectedType === 'partnership' || selectedType === 'office') && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      placeholder="+1 (555) 000-0000"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                </>
              )}

              {selectedType === 'office' && (
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Textarea
                    id="address"
                    placeholder="Full address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={2}
                  />
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={!formData.code || !formData.name || saving}
            >
              {saving ? 'Saving...' : editItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Import {selectedType.charAt(0).toUpperCase() + selectedType.slice(1)}s from CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk import {selectedType}s. Existing items with the same code will be updated.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 overflow-auto">
            <div className="space-y-4 py-4 pr-4">
            {/* Important Note */}
            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Note:</strong> Use <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">semicolon (;)</code> as the delimiter, not comma. This allows addresses and other fields to contain commas.
              </p>
            </div>

            {/* Template Download */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-sm font-medium">Download Template</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Get a CSV template with the correct column headers for {selectedType}s.
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Template
                </Button>
              </div>
            </div>

            {/* CSV Column Info */}
            <div className="text-sm">
              <h4 className="font-medium mb-2">Required columns:</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li><code className="bg-muted px-1 rounded">code</code> - Unique identifier (required)</li>
                <li><code className="bg-muted px-1 rounded">name</code> - Display name (required)</li>
                {selectedType === 'region' && (
                  <>
                    <li><code className="bg-muted px-1 rounded">country_code</code> - ISO country code (optional)</li>
                    <li><code className="bg-muted px-1 rounded">timezone</code> - Timezone (optional)</li>
                  </>
                )}
                {selectedType === 'office' && (
                  <>
                    <li><code className="bg-muted px-1 rounded">office_type</code> - "branch" or "franchise" (optional, defaults to branch)</li>
                    <li><code className="bg-muted px-1 rounded">region_code</code> - Link to region by code (optional)</li>
                    <li><code className="bg-muted px-1 rounded">address</code> - Office address (optional)</li>
                    <li><code className="bg-muted px-1 rounded">phone</code>, <code className="bg-muted px-1 rounded">email</code> - Contact info (optional)</li>
                  </>
                )}
                {selectedType === 'agent' && (
                  <>
                    <li><code className="bg-muted px-1 rounded">EmployeeKey</code> - Unique agent code (required)</li>
                    <li><code className="bg-muted px-1 rounded">FirstName</code>, <code className="bg-muted px-1 rounded">LastName</code> - Agent name (required)</li>
                    <li><code className="bg-muted px-1 rounded">OfficeKey</code> - Link to office by code (optional)</li>
                    <li><code className="bg-muted px-1 rounded">EmailAddress</code>, <code className="bg-muted px-1 rounded">MobileNumber</code> - Contact info (optional)</li>
                    <li><code className="bg-muted px-1 rounded">bitIsEmeraldCircle</code>, <code className="bg-muted px-1 rounded">bitIsGoldClub</code> - Recognition flags (True/False)</li>
                    <li><code className="bg-muted px-1 rounded">nvcRecognitionStatus</code> - Recognition status text (optional)</li>
                    <li><code className="bg-muted px-1 rounded">bitHasFFC</code>, <code className="bg-muted px-1 rounded">nvcFFCType</code> - FFC status (optional)</li>
                    <li><code className="bg-muted px-1 rounded">nvcHighResImageURL</code> - Profile image URL (optional)</li>
                    <li><code className="bg-muted px-1 rounded">RGBAgent*ImageURL</code> - Various image format URLs (optional)</li>
                  </>
                )}
              </ul>
            </div>

            {/* File Upload */}
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-file-input"
                data-testid="csv-file-input"
              />
              <FileSpreadsheet className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-3">
                {csvUploading ? 'Uploading...' : 'Select a CSV file to import'}
              </p>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={csvUploading}
                data-testid="csv-select-file-button"
              >
                {csvUploading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Select CSV File
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground mt-3">
                <strong>Note:</strong> Records not in the import file will be marked as inactive.
              </p>
            </div>

            {/* Import Results */}
            {csvResult && !csvResult.error && (
              <Alert className={
                csvResult.failed > 0 
                  ? "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950" 
                  : (csvResult.warnings?.length > 0 || csvResult.errors?.length > 0)
                    ? "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950"
                    : "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950"
              }>
                {csvResult.failed > 0 ? (
                  <XCircle className="h-4 w-4 text-red-600" />
                ) : (csvResult.warnings?.length > 0 || csvResult.errors?.length > 0) ? (
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                ) : (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                )}
                <AlertTitle className={
                  csvResult.failed > 0 
                    ? "text-red-800 dark:text-red-200"
                    : (csvResult.warnings?.length > 0 || csvResult.errors?.length > 0)
                      ? "text-amber-800 dark:text-amber-200"
                      : "text-green-800 dark:text-green-200"
                }>
                  {csvResult.failed > 0 
                    ? 'Import Completed with Errors' 
                    : (csvResult.warnings?.length > 0)
                      ? 'Import Completed with Warnings'
                      : 'Import Complete'
                  }
                </AlertTitle>
                <AlertDescription>
                  <ul className="mt-2 space-y-1 text-sm">
                    <li>Total rows processed: <strong>{csvResult.total_rows || 0}</strong></li>
                    <li className="text-green-700 dark:text-green-400">Created: <strong>{csvResult.created || 0}</strong></li>
                    <li className="text-blue-700 dark:text-blue-400">Updated: <strong>{csvResult.updated || 0}</strong></li>
                    {csvResult.reactivated > 0 && (
                      <li className="text-purple-700 dark:text-purple-400">Reactivated: <strong>{csvResult.reactivated}</strong></li>
                    )}
                    {csvResult.marked_inactive > 0 && (
                      <li className="text-amber-700 dark:text-amber-400">Marked Inactive: <strong>{csvResult.marked_inactive}</strong></li>
                    )}
                    {csvResult.failed > 0 && (
                      <li className="text-red-600 dark:text-red-400">Failed: <strong>{csvResult.failed}</strong></li>
                    )}
                  </ul>
                  
                  {/* Show warnings (unresolved relationships) */}
                  {csvResult.warnings && csvResult.warnings.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-amber-300 dark:border-amber-700">
                      <p className="font-medium text-amber-700 dark:text-amber-400 mb-2">
                        ⚠️ Warnings ({csvResult.warnings.length}):
                      </p>
                      <div className="max-h-32 overflow-y-auto space-y-2">
                        {csvResult.warnings.map((warn, idx) => (
                          <div key={idx} className="text-xs bg-amber-100 dark:bg-amber-900/30 p-2 rounded">
                            <p className="text-amber-800 dark:text-amber-300">
                              <strong>{warn.item?.code || 'Unknown'}</strong>: {warn.warning}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Show detailed errors */}
                  {csvResult.errors && csvResult.errors.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-red-300 dark:border-red-700">
                      <p className="font-medium text-red-700 dark:text-red-400 mb-2">
                        ❌ Errors ({csvResult.errors.length}):
                      </p>
                      <div className="max-h-32 overflow-y-auto space-y-2">
                        {csvResult.errors.map((err, idx) => (
                          <div key={idx} className="text-xs bg-red-100 dark:bg-red-900/30 p-2 rounded">
                            <p className="font-medium text-red-800 dark:text-red-300">
                              {err.item?.code || `Row ${idx + 1}`}: {err.error}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {csvResult?.error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Import Failed</AlertTitle>
                <AlertDescription>
                  <p>{csvResult.error}</p>
                  {typeof csvResult.error === 'string' && csvResult.error.includes('row') && (
                    <p className="mt-2 text-sm">Check your CSV file format and ensure all required columns are present.</p>
                  )}
                </AlertDescription>
              </Alert>
            )}
            </div>
          </ScrollArea>

          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setCsvDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// FTP Scheduler Component
function FTPSchedulerTab() {
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchConfig();
    fetchLogs();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await apiV1.get('/ftp-scheduler/config');
      if (response.data.success) {
        setConfig(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch FTP config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await apiV1.get('/ftp-scheduler/logs?limit=10');
      if (response.data.success) {
        setLogs(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch FTP logs:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiV1.put('/ftp-scheduler/config', config);
      if (response.data.success) {
        toast.success('FTP configuration saved');
        setConfig(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await apiV1.post('/ftp-scheduler/test-connection');
      setTestResult(response.data.data);
      if (response.data.success) {
        toast.success('Connection test successful');
      } else {
        toast.error('Connection test failed');
      }
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || 'Test failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleRunImport = async () => {
    setRunning(true);
    try {
      const response = await apiV1.post('/ftp-scheduler/run');
      if (response.data.success) {
        toast.success(response.data.message);
        fetchLogs();
        fetchConfig();
      } else {
        toast.error('Import failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setRunning(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      <div className={`rounded-lg p-4 ${config?.enabled ? 'bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800' : 'bg-muted'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Server className={`h-5 w-5 ${config?.enabled ? 'text-green-600' : 'text-muted-foreground'}`} />
            <div>
              <h3 className="font-medium">
                {config?.enabled ? 'Scheduler Active' : 'Scheduler Disabled'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {config?.enabled 
                  ? `Runs daily at ${String(config.schedule_hour).padStart(2, '0')}:${String(config.schedule_minute).padStart(2, '0')} UTC`
                  : 'Enable the scheduler to automatically import data from FTP'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {config?.mock_mode && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                Mock Mode
              </Badge>
            )}
            <Button
              onClick={handleRunImport}
              disabled={running}
              variant="outline"
              data-testid="ftp-run-import-button"
            >
              {running ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Now
            </Button>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Configuration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              FTP Configuration
            </CardTitle>
            <CardDescription>
              Configure the FTP server connection and import schedule
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Enable Switch */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Scheduled Imports</Label>
                <p className="text-sm text-muted-foreground">Automatically import daily</p>
              </div>
              <Switch
                checked={config?.enabled || false}
                onCheckedChange={(v) => updateConfig('enabled', v)}
                data-testid="ftp-enabled-switch"
              />
            </div>

            <div className="border-t pt-4 space-y-4">
              {/* Connection Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1 space-y-2">
                  <Label htmlFor="ftp-host">FTP Host</Label>
                  <Input
                    id="ftp-host"
                    placeholder="ftp.example.com"
                    value={config?.host || ''}
                    onChange={(e) => updateConfig('host', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ftp-port">Port</Label>
                  <Input
                    id="ftp-port"
                    type="number"
                    placeholder="21"
                    value={config?.port || 21}
                    onChange={(e) => updateConfig('port', parseInt(e.target.value) || 21)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ftp-username">Username</Label>
                  <Input
                    id="ftp-username"
                    placeholder="username"
                    value={config?.username || ''}
                    onChange={(e) => updateConfig('username', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ftp-password">Password</Label>
                  <Input
                    id="ftp-password"
                    type="password"
                    placeholder="••••••••"
                    value={config?.password || ''}
                    onChange={(e) => updateConfig('password', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ftp-path">Base Path</Label>
                <Input
                  id="ftp-path"
                  placeholder="/imports"
                  value={config?.base_path || ''}
                  onChange={(e) => updateConfig('base_path', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Directory containing CSV files</p>
              </div>

              {/* Schedule Settings */}
              <div className="border-t pt-4">
                <Label>Schedule Time (UTC)</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="space-y-2">
                    <Label htmlFor="schedule-hour" className="text-xs text-muted-foreground">Hour</Label>
                    <Select
                      value={String(config?.schedule_hour || 2)}
                      onValueChange={(v) => updateConfig('schedule_hour', parseInt(v))}
                    >
                      <SelectTrigger id="schedule-hour">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(24)].map((_, i) => (
                          <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="schedule-minute" className="text-xs text-muted-foreground">Minute</Label>
                    <Select
                      value={String(config?.schedule_minute || 0)}
                      onValueChange={(v) => updateConfig('schedule_minute', parseInt(v))}
                    >
                      <SelectTrigger id="schedule-minute">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 15, 30, 45].map((m) => (
                          <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Additional Options */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Use TLS (FTPS)</Label>
                    <p className="text-xs text-muted-foreground">Secure FTP connection</p>
                  </div>
                  <Switch
                    checked={config?.use_tls || false}
                    onCheckedChange={(v) => updateConfig('use_tls', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Delete After Import</Label>
                    <p className="text-xs text-muted-foreground">Remove files from FTP after processing</p>
                  </div>
                  <Switch
                    checked={config?.delete_after_import ?? true}
                    onCheckedChange={(v) => updateConfig('delete_after_import', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Mock Mode</Label>
                    <p className="text-xs text-muted-foreground">Use simulated FTP for testing</p>
                  </div>
                  <Switch
                    checked={config?.mock_mode ?? true}
                    onCheckedChange={(v) => updateConfig('mock_mode', v)}
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={handleTestConnection} variant="outline" disabled={testing}>
                {testing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                Test Connection
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save Configuration
              </Button>
            </div>

            {/* Test Result */}
            {testResult && (
              <Alert className={testResult.success ? 'border-green-200 bg-green-50' : ''} variant={testResult.success ? 'default' : 'destructive'}>
                {testResult.success ? <CheckCircle className="h-4 w-4 text-green-600" /> : <AlertCircle className="h-4 w-4" />}
                <AlertTitle>{testResult.success ? 'Connection Successful' : 'Connection Failed'}</AlertTitle>
                <AlertDescription>
                  {testResult.message}
                  {testResult.files_available && (
                    <p className="mt-1 text-xs">Files found: {testResult.files_available.join(', ')}</p>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* File Configuration & Import History */}
        <div className="space-y-6">
          {/* File Names Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                File Names
              </CardTitle>
              <CardDescription>
                Expected CSV file names on the FTP server
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="regions-file">Regions</Label>
                <Input
                  id="regions-file"
                  value={config?.regions_filename || 'regions.csv'}
                  onChange={(e) => updateConfig('regions_filename', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="offices-file">Offices</Label>
                <Input
                  id="offices-file"
                  value={config?.offices_filename || 'offices.csv'}
                  onChange={(e) => updateConfig('offices_filename', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="agents-file">Agents</Label>
                <Input
                  id="agents-file"
                  value={config?.agents_filename || 'agents.csv'}
                  onChange={(e) => updateConfig('agents_filename', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Import History Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Import History
              </CardTitle>
              <CardDescription>
                Recent FTP import runs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No import history yet. Run an import to see results here.
                </p>
              ) : (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div key={log.id} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {log.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-600" />}
                          {log.status === 'partial' && <AlertCircle className="h-4 w-4 text-amber-600" />}
                          {log.status === 'failed' && <XCircle className="h-4 w-4 text-red-600" />}
                          <span className="text-sm font-medium capitalize">{log.status}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(log.started_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground grid grid-cols-3 gap-2">
                        <div>Files: {log.total_files_processed}/{log.total_files_found}</div>
                        <div>Created: {log.total_records_created}</div>
                        <div>Updated: {log.total_records_updated}</div>
                      </div>
                      {log.triggered_by && (
                        <Badge variant="outline" className="mt-2 text-xs">
                          {log.triggered_by}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'viewer' });

  const getRoleBadge = (role) => {
    const classes = {
      admin: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      editor: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      viewer: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    };
    return (
      <Badge variant="outline" className={classes[role]}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    return (
      <Badge variant={status === 'active' ? 'default' : 'secondary'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredUsers = mockUsers.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleInvite = () => {
    toast.success(`Invitation sent to ${inviteForm.email}`);
    setInviteDialogOpen(false);
    setInviteForm({ email: '', name: '', role: 'viewer' });
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <RoleGate
      role="admin"
      fallback={
        <NotAuthorized
          title="Administration is admin-only"
          description="Editors and viewers can't see this area. Ask an administrator to update your role if you need access."
        />
      }
    >
    <div className="space-y-6" data-testid="admin-page">
      <PageHeader
        title="Administration"
        description="Manage users, reference data, and view audit logs"
      />

      <Tabs defaultValue="reference" className="space-y-4">
        <TabsList>
          <TabsTrigger value="reference" data-testid="admin-reference-tab">
            <Database className="h-4 w-4 mr-2" />
            Reference Data
          </TabsTrigger>
          <TabsTrigger value="ftp" data-testid="admin-ftp-tab">
            <Server className="h-4 w-4 mr-2" />
            FTP Import
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-users-tab">
            <Users className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="roles" data-testid="admin-roles-tab">
            <Shield className="h-4 w-4 mr-2" />
            Roles
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-audit-tab">
            <Clock className="h-4 w-4 mr-2" />
            Audit Logs
          </TabsTrigger>
        </TabsList>

        {/* Reference Data Tab */}
        <TabsContent value="reference" className="space-y-4">
          <ReferenceDataTab />
        </TabsContent>

        {/* FTP Import Tab */}
        <TabsContent value="ftp" className="space-y-4">
          <FTPSchedulerTab />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[140px]" data-testid="admin-role-select">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setInviteDialogOpen(true)} data-testid="admin-invite-user-button">
              <Plus className="h-4 w-4 mr-2" />
              Invite User
            </Button>
          </div>

          <div className="border rounded-lg" data-testid="admin-user-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>{getStatusBadge(user.status)}</TableCell>
                    <TableCell className="text-muted-foreground">{user.lastLogin}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit User
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Deactivate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            {['admin', 'editor', 'viewer'].map((role) => (
              <div key={role} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium capitalize">{role}</h3>
                  {getRoleBadge(role)}
                </div>
                <p className="text-sm text-muted-foreground">
                  {role === 'admin' && 'Full access to all features'}
                  {role === 'editor' && 'Create and manage QR codes'}
                  {role === 'viewer' && 'View-only access'}
                </p>
                <div className="text-sm">
                  <span className="text-muted-foreground">Users: </span>
                  <span className="font-medium">
                    {mockUsers.filter((u) => u.role === role).length}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit" className="space-y-4">
          <AuditLogTab />
        </TabsContent>
      </Tabs>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Send an invitation to join the platform
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="user@company.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-name">Full Name</Label>
              <Input
                id="invite-name"
                placeholder="John Doe"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={!inviteForm.email}>
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </RoleGate>
  );
}
